import { bind, Subscribe } from '@react-rxjs/core'
import React, { useContext, useEffect } from 'react'
import { combineLatest, debounceTime, map, Observable, of } from 'rxjs'
import { switchMap } from 'rxjs/operators'

import { CartFragment } from '../../../graphql/magento'
import { NonEmptyCartContext } from '../../../lib/cart/context'
import {
  placeOrderTaskApplicationLoading$,
  placeOrderTaskFromCart,
  placeOrderTaskState$,
  setPlaceOrderTaskInput,
} from '../../../lib/cart/state/order'
import { usePageUrl } from '../../../lib/pages'
import Spinner from '../../Spinner'

const steps = [
  {
    key: 'shipping',
    path: '/checkout/shipping',
    isComplete: (cart: CartFragment): Observable<boolean> => of(!!cart.shipping_addresses[0]),
  },
  {
    key: 'delivery',
    path: '/checkout/delivery',
    isComplete: (cart: CartFragment): Observable<boolean> =>
      of(!!cart.shipping_addresses[0]?.selected_shipping_method),
  },
  {
    key: 'payment',
    path: '/checkout/payment',
    isComplete: (cart: CartFragment): Observable<boolean> =>
      placeOrderTaskState$.pipe(
        switchMap((inputState) => {
          if (inputState === 'present') {
            return of(true)
          }
          // try to create place order task from cart if possible (e.g. payment method is already
          // set and is not CC)
          const placeOrderTask = placeOrderTaskFromCart(cart)
          if (!placeOrderTask) {
            return of(false)
          }
          setPlaceOrderTaskInput(placeOrderTask)
          return placeOrderTaskState$.pipe(
            debounceTime(100),
            map((updatedState) => updatedState === 'present'),
          )
        }),
      ),
  },
] as const

export type CartProgressStep = (typeof steps)[number]['key']

const incompleteStepsFactory$ = (
  cart: CartFragment,
  requiredSteps: ReadonlyArray<CartProgressStep>,
): Observable<{ key: string; path: string } | undefined> =>
  placeOrderTaskApplicationLoading$.pipe(
    switchMap((placeOrderInProgress) =>
      placeOrderInProgress
        ? of(undefined)
        : combineLatest(
            steps
              .filter(({ key }) => requiredSteps.includes(key))
              .map(({ isComplete, key, path }) =>
                isComplete(cart).pipe(
                  map((isCompleteResult) => ({ isComplete: isCompleteResult, key, path })),
                ),
              ),
          ).pipe(
            map((completionResults) => completionResults.find(({ isComplete }) => !isComplete)),
          ),
    ),
  )

const [useIncompleteSteps] = bind(incompleteStepsFactory$)

export interface RequiredCartProgressProps {
  children: React.ReactNode
  required: ReadonlyArray<CartProgressStep>
}

const RequiredCheckoutProgressReactive: React.FC<RequiredCartProgressProps> = ({
  children,
  required,
}) => {
  const customerCart = useContext(NonEmptyCartContext)
  const { navigate } = usePageUrl()

  const incompleteStep = useIncompleteSteps(customerCart, required)

  useEffect(() => {
    if (incompleteStep) {
      navigate(incompleteStep.path)
    }
  }, [incompleteStep, navigate])

  return incompleteStep ? <Spinner loading /> : <React.Fragment>{children}</React.Fragment>
}

const RequiredCheckoutProgress: React.FC<RequiredCartProgressProps> = ({ children, required }) => (
  <Subscribe fallback={<Spinner loading />}>
    <RequiredCheckoutProgressReactive required={required}>
      {children}
    </RequiredCheckoutProgressReactive>
  </Subscribe>
)

export default RequiredCheckoutProgress
